import { useCallback, useMemo, useState } from 'react'
import { Icon } from '@iconify/react'
import type {
  RaspadinhaContemplatedPrizeImage,
  RaspadinhaDayWinningSchedule,
} from '@/types/raspadinha-campaign'
import {
  buildContemplatedPrizeUsageSummary,
  countTotalConfiguredTimeSlots,
  ensureDateWinningSchedules,
} from '@/lib/raspadinha-campaigns'
import { RaspadinhaContemplatedPrizeProgressBar } from './RaspadinhaContemplatedPrizeProgressBar'
import styles from './RaspadinhaContemplatedPrizeUsageSummary.module.css'

type RaspadinhaContemplatedPrizeUsageSummaryProps = {
  schedules: RaspadinhaDayWinningSchedule[]
  contemplatedPrizes: RaspadinhaContemplatedPrizeImage[]
  startsAt: string | null
  endsAt: string | null
}

export function RaspadinhaContemplatedPrizeUsageSummary({
  schedules,
  contemplatedPrizes,
  startsAt,
  endsAt,
}: RaspadinhaContemplatedPrizeUsageSummaryProps) {
  const [isExpanded, setIsExpanded] = useState(false)

  const resolvedSchedules = useMemo(
    () => ensureDateWinningSchedules(schedules, startsAt, endsAt),
    [schedules, startsAt, endsAt]
  )

  const usageRows = useMemo(
    () => buildContemplatedPrizeUsageSummary(resolvedSchedules, contemplatedPrizes),
    [contemplatedPrizes, resolvedSchedules]
  )

  const totalConfiguredSlots = useMemo(
    () => countTotalConfiguredTimeSlots(resolvedSchedules),
    [resolvedSchedules]
  )

  const handleToggleExpanded = useCallback(() => {
    setIsExpanded((previous) => !previous)
  }, [])

  if (resolvedSchedules.length === 0) {
    return null
  }

  const collapsedDescription =
    usageRows.length === 0
      ? 'Nenhum prêmio com título cadastrado em Mídia e prêmios.'
      : `${totalConfiguredSlots} horário(s) configurado(s) em ${usageRows.length} prêmio(s).`

  return (
    <section
      className={`${styles.card} ${isExpanded ? styles.cardExpanded : styles.cardCollapsed}`}
      aria-labelledby="contemplated-prize-usage-title"
    >
      <div className={styles.cardHeader}>
        <div className={styles.cardHeaderMain}>
          <div className={styles.cardTitleRow}>
            <Icon icon="mdi:chart-box-outline" className={styles.cardTitleIcon} aria-hidden />
            <h3 id="contemplated-prize-usage-title" className={styles.cardTitle}>
              Resumo de prêmios nos horários
            </h3>
            {!isExpanded && totalConfiguredSlots > 0 ? (
              <span className={styles.summaryBadge}>{totalConfiguredSlots} horário(s)</span>
            ) : null}
          </div>
          <p className={styles.cardDescription}>
            {isExpanded
              ? 'Visualize quantas vezes cada prêmio está vinculado aos horários de contemplação da campanha.'
              : collapsedDescription}
          </p>
        </div>

        <button
          type="button"
          className={styles.expandButton}
          onClick={handleToggleExpanded}
          aria-expanded={isExpanded}
          aria-controls="contemplated-prize-usage-panel"
        >
          <span>{isExpanded ? 'Recolher' : 'Expandir'}</span>
          <Icon
            icon="mdi:chevron-down"
            className={`${styles.expandButtonIcon} ${isExpanded ? styles.expandButtonIconExpanded : ''}`}
            aria-hidden
          />
        </button>
      </div>

      {isExpanded ? (
        <div id="contemplated-prize-usage-panel" className={styles.cardBody}>
          <p className={styles.summaryMeta}>
            {totalConfiguredSlots} horário(s) configurado(s) no total.
          </p>

          {usageRows.length === 0 ? (
            <p className={styles.emptyState}>
              Nenhum prêmio com título cadastrado em <strong>Mídia e prêmios</strong>.
            </p>
          ) : (
            <ul className={styles.prizeList}>
              {usageRows.map((row) => (
                <li
                  key={row.prizeId}
                  className={`${styles.prizeRow} ${!row.isConfiguredPrize ? styles.prizeRowWarning : ''}`}
                >
                  <div className={styles.prizeRowMain}>
                    {row.imageUrl.trim() ? (
                      <img src={row.imageUrl} alt="" className={styles.prizeThumb} />
                    ) : (
                      <span className={styles.prizeThumbPlaceholder} aria-hidden>
                        <Icon icon="mdi:gift-outline" />
                      </span>
                    )}

                    <div className={styles.prizeInfo}>
                      <span className={styles.prizeTitle}>{row.title}</span>
                      <span className={styles.prizeMeta}>
                        {row.maxContemplations !== null
                          ? `Limite de contemplações: ${row.maxContemplations}`
                          : 'Horário sem prêmio vinculado'}
                      </span>
                    </div>

                    <span
                      className={`${styles.prizeCount} ${row.configuredSlotCount === 0 ? styles.prizeCountZero : ''}`}
                      aria-label={`${row.configuredSlotCount} horário(s) com este prêmio`}
                    >
                      {row.configuredSlotCount}
                    </span>
                  </div>

                  <RaspadinhaContemplatedPrizeProgressBar
                    fulfilledCount={row.fulfilledCount}
                    scheduledCount={row.configuredSlotCount}
                    prizeLabel={row.title}
                  />
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </section>
  )
}
